import React, { useRef, useState } from "react";
import { useField } from "formik";
import { Box, FormControl, Popover, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { FieldError } from "../FieldError/FieldError";
import { CustomFieldLabel } from "../CustomFieldLabel/CustomFieldLabel";
import { IconCalendar, IconCross } from "../../../utils/icons";

interface Props {
    name: string;
    label?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    minDate?: Dayjs;
}

const DISPLAY_FORMAT = "MM/DD/YYYY";
const ICON_GREY = "#6B6B6B";

const shellSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    height: "50px",
    px: "10px",
    borderRadius: "12px",
    backgroundColor: "var(--Base-24, #F4F4F4)",
    cursor: "pointer",
    transition: "background-color 120ms ease",
    "&:hover": { backgroundColor: "var(--Base-32, #EFEFEF)" },
};

const textSx = {
    fontFamily: "CircularStd",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "normal",
    color: "var(--Base-1000, #000)",
};

const placeholderSx = { ...textSx, color: "var(--muted, #8A8A8A)" };

const calendarSx = {
    "& .MuiPickersDay-root": {
        fontFamily: "CircularStd",
        fontSize: "13px",
        "&.Mui-selected": {
            backgroundColor: "#A58450",
            color: "#fff",
            "&:hover, &:focus": { backgroundColor: "#80582A" },
        },
    },
    "& .MuiDayCalendar-weekDayLabel": {
        fontFamily: "CircularStd",
        fontSize: "12px",
        color: "var(--text-2)",
    },
    "& .MuiPickersCalendarHeader-label": {
        fontFamily: "CircularStd",
        fontSize: "14px",
        fontWeight: 600,
    },
};

export function CustomFieldCalendar({
    name,
    label,
    placeholder = "Select a date",
    required = false,
    disabled,
    minDate,
}: Props) {
    const [field, meta, helpers] = useField(name);
    const iconRef = useRef<HTMLDivElement | null>(null);
    const [open, setOpen] = useState(false);
    const value = field.value ? dayjs(field.value) : null;

    const close = () => {
        setOpen(false);
        helpers.setTouched(true);
    };

    return (
        <FormControl className="field-container" fullWidth>
            {label && (
                <CustomFieldLabel required={required} disabled={disabled}>
                    {label}
                </CustomFieldLabel>
            )}
            <Box
                sx={{ ...shellSx, ...(disabled ? { opacity: 0.6, cursor: "default" } : {}) }}
                onClick={() => !disabled && setOpen(true)}
            >
                <Typography sx={value?.isValid() ? textSx : placeholderSx}>
                    {value?.isValid() ? value.format(DISPLAY_FORMAT) : placeholder}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {value?.isValid() && !disabled && (
                        <Box
                            sx={{ display: "flex", alignItems: "center" }}
                            onClick={(event) => {
                                event.stopPropagation();
                                helpers.setValue(null);
                            }}
                        >
                            <IconCross width={16} height={16} stroke={ICON_GREY} />
                        </Box>
                    )}
                    <Box ref={iconRef} sx={{ display: "flex", alignItems: "center" }}>
                        <IconCalendar size={18} fill={ICON_GREY} />
                    </Box>
                </Box>
            </Box>
            <Popover
                open={open}
                anchorEl={iconRef.current}
                onClose={close}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            borderRadius: "16px",
                            border: "1px solid var(--border)",
                            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
                        },
                    },
                }}
            >
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-us">
                    <DateCalendar
                        value={value?.isValid() ? value : null}
                        minDate={minDate}
                        onChange={(next: Dayjs | null) => {
                            helpers.setValue(next);
                            close();
                        }}
                        sx={calendarSx}
                    />
                </LocalizationProvider>
            </Popover>
            {!!meta.touched && !!meta.error && <FieldError>{meta.error}</FieldError>}
        </FormControl>
    );
}
